import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { EnvelopeSimple, Trash, Eye, EyeSlash, ArrowClockwise, SignOut, LockKey, Broadcast, Calendar, FloppyDisk } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

export const OwnerPage = () => {
    const navigate = useNavigate();
    const [code, setCode] = useState(sessionStorage.getItem("ool_owner_code") || "");
    const [authed, setAuthed] = useState(false);
    const [unlockInput, setUnlockInput] = useState("");
    const [submissions, setSubmissions] = useState([]);
    const [stats, setStats] = useState({ total: 0, unread: 0 });
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [liveOverride, setLiveOverride] = useState("auto");
    const [savingOverride, setSavingOverride] = useState(false);
    const [scheduleDraft, setScheduleDraft] = useState([]);
    const [scheduleDirty, setScheduleDirty] = useState(false);
    const [savingSchedule, setSavingSchedule] = useState(false);

    const verify = async (codeToTry) => {
        try {
            await axios.post(`${API}/owner/verify`, { code: codeToTry });
            sessionStorage.setItem("ool_owner_code", codeToTry);
            setCode(codeToTry);
            setAuthed(true);
            return true;
        } catch {
            return false;
        }
    };

    const load = async (codeToUse) => {
        setLoading(true);
        try {
            const [list, st, ov, sch] = await Promise.all([
                axios.get(`${API}/contact?code=${encodeURIComponent(codeToUse)}`),
                axios.get(`${API}/contact/stats?code=${encodeURIComponent(codeToUse)}`),
                axios.get(`${API}/twitch/live-override`),
                axios.get(`${API}/schedule`),
            ]);
            setSubmissions(list.data || []);
            setStats(st.data || { total: 0, unread: 0 });
            setLiveOverride((ov.data && ov.data.state) || "auto");
            setScheduleDraft((sch.data && sch.data.days) || []);
            setScheduleDirty(false);
        } catch (err) {
            toast.error("Failed to load owner data.");
        } finally {
            setLoading(false);
        }
    };

    const updateDay = (idx, patch) => {
        setScheduleDraft((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
        setScheduleDirty(true);
    };

    const saveSchedule = async () => {
        setSavingSchedule(true);
        try {
            await axios.put(`${API}/schedule`, { days: scheduleDraft, code });
            toast.success("Schedule saved. Live on the site.");
            setScheduleDirty(false);
            window.dispatchEvent(new CustomEvent("ool:schedule-changed"));
        } catch {
            toast.error("Couldn't save schedule.");
        } finally {
            setSavingSchedule(false);
        }
    };

    const setOverride = async (state) => {
        setSavingOverride(true);
        try {
            await axios.post(`${API}/twitch/live-override`, { state, code });
            setLiveOverride(state);
            const labels = { auto: "Auto (real Twitch status)", live: "Forced LIVE", offline: "Forced OFFLINE" };
            toast.success(`Live status: ${labels[state]}`);
            // Nudge any open useLiveStatus consumers to refresh on next interval
            window.dispatchEvent(new CustomEvent("ool:live-override-changed", { detail: { state } }));
        } catch {
            toast.error("Couldn't update live status.");
        } finally {
            setSavingOverride(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            if (code) {
                const ok = await verify(code);
                if (ok) load(code);
                else {
                    sessionStorage.removeItem("ool_owner_code");
                    setCode("");
                }
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onUnlock = async (e) => {
        e.preventDefault();
        const ok = await verify(unlockInput.trim());
        if (ok) {
            toast.success("Access granted.");
            load(unlockInput.trim());
        } else {
            toast.error("Invalid code.");
        }
    };

    const onLogout = () => {
        sessionStorage.removeItem("ool_owner_code");
        setAuthed(false);
        setCode("");
        setSubmissions([]);
        toast("Logged out.");
        navigate("/");
    };

    const onMarkRead = async (id) => {
        try {
            await axios.patch(`${API}/contact/${id}/read?code=${encodeURIComponent(code)}`);
            setSubmissions((s) => s.map((it) => (it.id === id ? { ...it, read: true } : it)));
            setStats((st) => ({ ...st, unread: Math.max(0, st.unread - 1) }));
        } catch {
            toast.error("Couldn't update.");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this submission?")) return;
        try {
            await axios.delete(`${API}/contact/${id}?code=${encodeURIComponent(code)}`);
            const wasUnread = !submissions.find((s) => s.id === id)?.read;
            setSubmissions((s) => s.filter((it) => it.id !== id));
            setStats((st) => ({
                total: Math.max(0, st.total - 1),
                unread: wasUnread ? Math.max(0, st.unread - 1) : st.unread,
            }));
            toast.success("Deleted.");
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    // Unlock screen
    if (!authed) {
        return (
            <div data-testid="owner-page-locked">
                <PageHeader
                    eyebrow="Owner panel"
                    title="Restricted"
                    accent="area."
                    intro="Enter the owner code to view contact submissions. Hint: this page is also accessible via a keyboard shortcut."
                    breadcrumbs={[{ label: "Home", href: "/" }, { label: "Owner" }]}
                />
                <section className="py-10">
                    <div className="max-w-md mx-auto px-6">
                        <form onSubmit={onUnlock} className="rounded-2xl border border-white/10 bg-obsidian-900/60 p-7" data-testid="owner-unlock-form">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                                    <LockKey size={18} className="text-cyan-300" weight="bold" />
                                </div>
                                <div>
                                    <div className="eyebrow text-cyan-300/70">Owner access</div>
                                    <div className="font-display text-white text-lg">Enter code</div>
                                </div>
                            </div>
                            <input
                                type="password"
                                inputMode="numeric"
                                autoFocus
                                value={unlockInput}
                                onChange={(e) => setUnlockInput(e.target.value)}
                                placeholder="• • •"
                                className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-center font-mono text-xl tracking-[0.5em] focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
                                data-testid="owner-unlock-input"
                            />
                            <button
                                type="submit"
                                className="mt-5 w-full py-3 rounded-xl bg-white text-black font-semibold hover:bg-brand-cyan transition"
                                data-testid="owner-unlock-submit"
                            >
                                Unlock
                            </button>
                        </form>
                    </div>
                </section>
            </div>
        );
    }

    // Authed - panel
    return (
        <div data-testid="owner-page">
            <PageHeader
                eyebrow="Owner panel"
                title="Contact"
                accent="inbox."
                intro="Every submission from /contact lands here. Mark as read, dive into the message, or clear it out."
                breadcrumbs={[{ label: "Home", href: "/" }, { label: "Owner" }]}
            />

            <section className="py-10">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    {/* Live status override card */}
                    <div
                        className="rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-500/[0.07] via-obsidian-900/60 to-obsidian-900/60 p-5 lg:p-6 mb-6"
                        data-testid="owner-live-override-card"
                    >
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="h-11 w-11 rounded-xl bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
                                <Broadcast size={20} className="text-cyan-300" weight="bold" />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <div className="eyebrow text-cyan-300/80">Live status override</div>
                                <div className="font-display text-white text-lg mt-0.5">Are you streaming today?</div>
                                <p className="text-white/55 text-sm mt-1 max-w-xl">
                                    Toggle this and the LIVE badge, hero, and stream banner update across the site instantly.
                                    Pick <span className="text-white">Auto</span> to let the real Twitch status take over again.
                                </p>
                            </div>
                            <div
                                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.22em] text-white/65 font-mono self-start"
                                data-testid="owner-live-current"
                            >
                                Current: <span className={
                                    liveOverride === "live" ? "text-red-300" :
                                    liveOverride === "offline" ? "text-white/70" : "text-cyan-300"
                                }>{liveOverride}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-5">
                            <button
                                onClick={() => setOverride("auto")}
                                disabled={savingOverride || liveOverride === "auto"}
                                className={`py-2.5 px-3 rounded-xl text-sm font-semibold border transition disabled:opacity-100 disabled:cursor-default ${
                                    liveOverride === "auto"
                                        ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-200 ring-2 ring-cyan-400/30"
                                        : "bg-white/5 border-white/10 text-white/75 hover:border-white/30 hover:text-white"
                                }`}
                                data-testid="owner-live-auto"
                            >
                                Auto
                            </button>
                            <button
                                onClick={() => setOverride("live")}
                                disabled={savingOverride || liveOverride === "live"}
                                className={`py-2.5 px-3 rounded-xl text-sm font-semibold border transition disabled:opacity-100 disabled:cursor-default ${
                                    liveOverride === "live"
                                        ? "bg-red-500/20 border-red-400/60 text-red-200 ring-2 ring-red-400/30"
                                        : "bg-white/5 border-white/10 text-white/75 hover:border-red-400/30 hover:text-red-200"
                                }`}
                                data-testid="owner-live-force-live"
                            >
                                Force LIVE
                            </button>
                            <button
                                onClick={() => setOverride("offline")}
                                disabled={savingOverride || liveOverride === "offline"}
                                className={`py-2.5 px-3 rounded-xl text-sm font-semibold border transition disabled:opacity-100 disabled:cursor-default ${
                                    liveOverride === "offline"
                                        ? "bg-white/15 border-white/40 text-white ring-2 ring-white/20"
                                        : "bg-white/5 border-white/10 text-white/75 hover:border-white/30 hover:text-white"
                                }`}
                                data-testid="owner-live-force-offline"
                            >
                                Force OFFLINE
                            </button>
                        </div>
                    </div>

                    {/* Weekly schedule editor */}
                    <div
                        className="rounded-2xl border border-white/10 bg-obsidian-900/60 p-5 lg:p-6 mb-6"
                        data-testid="owner-schedule-editor"
                    >
                        <div className="flex items-start gap-4 flex-wrap mb-5">
                            <div className="h-11 w-11 rounded-xl bg-violet-500/15 border border-violet-500/40 flex items-center justify-center shrink-0">
                                <Calendar size={20} className="text-violet-300" weight="bold" />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <div className="eyebrow text-violet-300/85">Weekly schedule</div>
                                <div className="font-display text-white text-lg mt-0.5">Plan each day of the week</div>
                                <p className="text-white/55 text-sm mt-1 max-w-xl">
                                    Set what each stream is about (e.g. "Subathon", "Just Chatting", "Community Games") and toggle which days you're streaming. Changes go live the second you save.
                                </p>
                            </div>
                            <button
                                onClick={saveSchedule}
                                disabled={!scheduleDirty || savingSchedule}
                                className={`self-start inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
                                    scheduleDirty
                                        ? "bg-brand-cyan text-black hover:bg-cyan-200"
                                        : "bg-white/5 border border-white/10 text-white/40 cursor-not-allowed"
                                }`}
                                data-testid="owner-schedule-save"
                            >
                                <FloppyDisk size={13} weight="fill" />
                                {savingSchedule ? "Saving…" : scheduleDirty ? "Save changes" : "Saved"}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                            {scheduleDraft.map((d, idx) => (
                                <div
                                    key={d.dayIdx}
                                    className={`grid grid-cols-1 lg:grid-cols-[80px_1fr_1.4fr_auto] items-center gap-3 rounded-xl p-3 border ${
                                        d.off ? "border-white/5 bg-white/[0.015] opacity-70" : "border-white/10 bg-white/[0.025]"
                                    }`}
                                    data-testid={`owner-schedule-row-${d.day.toLowerCase()}`}
                                >
                                    <div className="font-display text-white text-base flex items-center gap-2">
                                        <span className={`h-1.5 w-1.5 rounded-full ${d.off ? "bg-white/30" : "bg-brand-cyan"}`} />
                                        {d.day}
                                    </div>
                                    <input
                                        type="text"
                                        value={d.category}
                                        onChange={(e) => updateDay(idx, { category: e.target.value })}
                                        placeholder="Category (Subathon, Variety, …)"
                                        disabled={d.off}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-brand-cyan/60 focus:outline-none focus:ring-2 focus:ring-brand-cyan/20 disabled:opacity-50"
                                        data-testid={`owner-schedule-cat-${d.day.toLowerCase()}`}
                                    />
                                    <input
                                        type="text"
                                        value={d.note}
                                        onChange={(e) => updateDay(idx, { note: e.target.value })}
                                        placeholder="Quick note shown under the category"
                                        disabled={d.off}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white/85 text-sm focus:border-brand-cyan/60 focus:outline-none focus:ring-2 focus:ring-brand-cyan/20 disabled:opacity-50"
                                        data-testid={`owner-schedule-note-${d.day.toLowerCase()}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => updateDay(idx, { off: !d.off })}
                                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                                            d.off
                                                ? "bg-white/5 border-white/15 text-white/60 hover:text-white"
                                                : "bg-emerald-500/15 border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25"
                                        }`}
                                        data-testid={`owner-schedule-toggle-${d.day.toLowerCase()}`}
                                    >
                                        <span className={`h-2 w-2 rounded-full ${d.off ? "bg-white/40" : "bg-emerald-400"}`} />
                                        {d.off ? "Off day" : "Streaming"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-3" data-testid="owner-stats">
                            <div className="px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.03]">
                                <div className="eyebrow">Total</div>
                                <div className="font-display text-xl text-white mt-1">{stats.total}</div>
                            </div>
                            <div className="px-4 py-2.5 rounded-xl border border-brand-cyan/30 bg-brand-cyan/[0.06]">
                                <div className="eyebrow text-brand-cyan">Unread</div>
                                <div className="font-display text-xl text-white mt-1">{stats.unread}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => load(code)}
                                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 text-xs hover:border-white/30 transition"
                                data-testid="owner-refresh-btn"
                            >
                                <ArrowClockwise size={12} weight="bold" /> Refresh
                            </button>
                            <button
                                onClick={onLogout}
                                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 text-xs hover:border-red-400/30 hover:text-red-300 transition"
                                data-testid="owner-logout-btn"
                            >
                                <SignOut size={12} weight="bold" /> Logout
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center text-white/40 py-10" data-testid="owner-loading">Loading...</div>
                    ) : submissions.length === 0 ? (
                        <div
                            className="rounded-2xl border border-white/10 bg-obsidian-900/60 p-10 text-center"
                            data-testid="owner-empty"
                        >
                            <EnvelopeSimple size={28} weight="fill" className="text-white/30 mx-auto mb-4" />
                            <div className="font-display text-lg text-white">No submissions yet</div>
                            <p className="text-white/50 text-sm mt-2">When someone hits the contact form, they'll show up here.</p>
                        </div>
                    ) : (
                        <div className="space-y-2" data-testid="owner-submissions">
                            {submissions.map((s) => {
                                const isOpen = expanded === s.id;
                                return (
                                    <motion.div
                                        key={s.id}
                                        layout
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`rounded-xl border overflow-hidden bg-obsidian-900/60 ${
                                            s.read ? "border-white/10" : "border-brand-cyan/30"
                                        }`}
                                        data-testid={`owner-submission-${s.id}`}
                                    >
                                        <button
                                            onClick={() => {
                                                setExpanded(isOpen ? null : s.id);
                                                if (!s.read) onMarkRead(s.id);
                                            }}
                                            className="w-full text-left p-4 flex items-start gap-3 hover:bg-white/[0.02]"
                                        >
                                            {!s.read && (
                                                <span className="h-2 w-2 rounded-full bg-brand-cyan mt-2 shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    <span className="font-display text-white text-sm">{s.name}</span>
                                                    <span className="font-mono text-xs text-white/40">{s.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[10px] uppercase tracking-wider text-brand-purple font-medium">{s.topic}</span>
                                                    <span className="text-white/20">·</span>
                                                    <span className="text-[11px] text-white/45 font-mono">{formatDate(s.created_at)}</span>
                                                </div>
                                                {!isOpen && (
                                                    <p className="text-sm text-white/55 mt-2 line-clamp-1">{s.message}</p>
                                                )}
                                            </div>
                                        </button>
                                        {isOpen && (
                                            <div className="px-4 pb-4 border-t border-white/5 pt-3">
                                                <p className="text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{s.message}</p>
                                                <div className="flex flex-wrap items-center gap-2 mt-4">
                                                    <a
                                                        href={`mailto:${s.email}?subject=Re:%20${encodeURIComponent(s.topic)}`}
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black text-xs font-semibold hover:bg-brand-cyan transition"
                                                        data-testid={`owner-reply-${s.id}`}
                                                    >
                                                        <EnvelopeSimple size={12} weight="fill" /> Reply
                                                    </a>
                                                    <button
                                                        onClick={() => onMarkRead(s.id)}
                                                        disabled={s.read}
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs hover:border-white/30 disabled:opacity-40 transition"
                                                        data-testid={`owner-mark-read-${s.id}`}
                                                    >
                                                        {s.read ? <EyeSlash size={12} /> : <Eye size={12} />}
                                                        {s.read ? "Read" : "Mark read"}
                                                    </button>
                                                    <button
                                                        onClick={() => onDelete(s.id)}
                                                        className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs hover:border-red-400/30 hover:text-red-300 transition"
                                                        data-testid={`owner-delete-${s.id}`}
                                                    >
                                                        <Trash size={12} weight="bold" /> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};
